import {
  combineLatest,
  distinctUntilChanged,
  fromEvent,
  map,
  shareReplay,
  startWith,
  type Observable,
} from 'rxjs';
import { isEqual } from '@ardoq/common-helpers';
import type { AssistantDisplayMode, HistoryPlacement } from './assistantTypes';
import { getScopedAssistantState } from './getScopedAssistantState';

const STANDALONE_HISTORY_PANEL_MIN_WIDTH = 740;

export const toHistoryPlacement = (
  displayMode: AssistantDisplayMode,
  viewportWidth: number
): HistoryPlacement => {
  if (displayMode === 'fullScreen') return 'panel';
  if (displayMode !== 'standalone') return 'screen';
  return viewportWidth >= STANDALONE_HISTORY_PANEL_MIN_WIDTH
    ? 'panel'
    : 'screen';
};

const viewportWidth$ = fromEvent(window, 'resize').pipe(
  map(() => window.innerWidth),
  startWith(window.innerWidth),
  distinctUntilChanged()
);

export type AssistantLayout = {
  displayMode: AssistantDisplayMode;
  historyPlacement: HistoryPlacement;
};

/**
 * The mode and the placement it implies, emitted together. A view model that
 * needs either must take both from here rather than reading `displayMode` from
 * its own scoped state: both would derive from `assistantState$`, and
 * `combineLatest` would emit the new mode beside the old placement first.
 */
export const assistantLayout$: Observable<AssistantLayout> = combineLatest([
  getScopedAssistantState(['displayMode']),
  viewportWidth$,
]).pipe(
  map(([{ displayMode }, viewportWidth]) => ({
    displayMode,
    historyPlacement: toHistoryPlacement(displayMode, viewportWidth),
  })),
  distinctUntilChanged(isEqual),
  shareReplay({ bufferSize: 1, refCount: true })
);
